import { Typography } from '@mui/material';
import React, { useState, useEffect, useContext } from 'react';
import Content from '../components/Layout/Content';
import ReusableTable from '../components/ReusableTable/ReusableTable';
import { Box } from '@mui/system';
import FilterDropDown from '../components/filterDropDown/FilterDropDown';
import SearchBar from '../components/SearchBar/Search';
import NotFound from '../components/NotFound/NotFoundPage';
import PaginationComponent from '../components/paginationComponent/ServerSidePaginationComponent';
import {
  socialHistoryArrayConverter,
  getIconFromSubItemsArray,
  calculateDateFilterTimeStamp,
  getStatusFilterParamList,
} from '../shared/helper';
import {
  smokingStatusFilterDatas,
  dateFilterMenuItems,
  NUMBER_OF_ROWS_PER_PAGE,
} from '../shared/constants';
import { BreadcrumbComponent } from '../components/BreadcrumbComponent/BreadcrumbComponent';
import { useSnackbar } from 'notistack';
import { useLazyQuery } from '@apollo/client';
import { SMOKING_STATUS_QUERY } from '../services/SmokingStatusQueries/SmokingStatusService';
import { SimpleBackdrop } from '../components/SimpleBackdropComponent/SimpleBackdrop';
import { ServiceFlyout } from '../components/ServiceFlyout/ServiceFlyout';
import ApplicationContext from '../context/applicationContext';
import {
  SOCIAL_HISTORY,
  SOCIAL_HISTORY_TABLE_HEADING as tableHeading,
  SOCIAL_HISTORY_BREADCRUMBDATA as breadcrumbData,
} from '../shared/Strings';

const boxContainer = {
  position: 'relative',
  display: 'flex',
  flexWrap: 'wrap',
  top: 40,
  flex: 1,
};

const table = {
  marginTop: 8,
  '& .css-1kn3lhz': {},
};

export const SmokingStatus = () => {
  const [openFlyout, setOpenFlyout] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [recordsData, setRecordedData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [timeFilter, setTimeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [detailsData, setDetailsData] = useState([]);
  const [modalTitle, setModalTitle] = useState('');
  const [totalCount, setTotalCount] = useState();
  const [offset, setOffset] = useState(0);
  const { menuItems } = useContext(ApplicationContext);
  const { enqueueSnackbar } = useSnackbar();

  const [fetchAllSocialHistory, fetchAllSocialHistoryCall] = useLazyQuery(
    SMOKING_STATUS_QUERY,
    {
      fetchPolicy: 'cache-and-network',
      onError: err => {
        enqueueSnackbar(SOCIAL_HISTORY.ON_ERROR_TEXT.WENT_WRONG_TEXT, {
          variant: 'error',
        });
      },
      onCompleted: res => {
        let result = res.fetchSmokingStatus.smokingStatusList;
        let convetor = JSON.parse(result);
        let data = socialHistoryArrayConverter(convetor);
        setRecordedData(data);
        setFilteredData(data);
        setTotalCount(res.fetchSmokingStatus.count);
      },
    }
  );

  useEffect(() => {
    fetchAllSocialHistory({
      variables: {
        sourceUrl: '',
        count: NUMBER_OF_ROWS_PER_PAGE.toString(),
        offset: offset,
        dateFilter: calculateDateFilterTimeStamp(timeFilter) || 0,
        statusFilter: getStatusFilterParamList(
          statusFilter,
          smokingStatusFilterDatas,
          smokingStatusFilterDatas
        ),
        searchFilter: searchValue,
      },
    });
  }, [offset, searchValue, timeFilter, statusFilter]);

  const onPageClick = pageNumber => {
    setOffset((pageNumber - 1) * 10);
  };

  const closeFlyout = () => {
    setOpenFlyout(false);
  };

  const onSearch = text => {
    setSearchValue(text);
  };

  const handleChangeFilter = () => {
    setOffset(0);
  };

  const dateFilterOptions = {
    options: dateFilterMenuItems,
    value: timeFilter,
    changeHandler: ev => {
      setTimeFilter(ev.target.value);
      handleChangeFilter();
    },
  };

  const statusFilterOptions = {
    options: smokingStatusFilterDatas,
    value: statusFilter,
    changeHandler: ev => {
      setStatusFilter(ev.target.value);
      handleChangeFilter();
    },
  };

  const viewDetailsHandler = id => {
    const tableRowInfo = recordsData.find(obj => obj.id === id);
    setModalTitle(tableRowInfo.name);
    const modalData = [
      {
        column_name: SOCIAL_HISTORY.MODAL_DATA.STATUS,
        value: tableRowInfo.status,
      },
      {
        column_name: SOCIAL_HISTORY.MODAL_DATA.RECORDED_ON,
        value: tableRowInfo.recordedOn,
        width: 6,
      },
      {
        column_name: SOCIAL_HISTORY.MODAL_DATA.RECORDED_BY,
        value: tableRowInfo.recordedBy,
      },
      {
        column_name: SOCIAL_HISTORY.MODAL_DATA.FREQUENCY,
        value: tableRowInfo.frequency,
      },
    ];
    setDetailsData(modalData);
    setOpenFlyout(true);
  };

  return (
    <Content>
      <BreadcrumbComponent
        breadcrumbData={breadcrumbData}></BreadcrumbComponent>
      <Typography variant="title" data-testid="heading">
        {SOCIAL_HISTORY.TITLE_TEXT}
      </Typography>
      <Box sx={boxContainer}>
        <SearchBar
          sx={{
            width: '50%',
            minWidth: '250px',
            maxWidth: '500px',
            marginTop: '8px',
          }}
          onSearch={onSearch}
          placeholder={SOCIAL_HISTORY.SEARCH_PLACEHOLDER_TEXT}
          setOffset={setOffset}
        />
        <FilterDropDown
          data-testid="datefilter"
          {...dateFilterOptions}
          filterType="Date:"
        />
        <FilterDropDown
          data-testid="statusfilter"
          {...statusFilterOptions}
          filterType="Status:"
        />
      </Box>

      <Box sx={table}>
        {!fetchAllSocialHistoryCall.loading && !filteredData.length ? (
          <NotFound title={SOCIAL_HISTORY.TITLE_TEXT} />
        ) : (
          <>
            <ReusableTable
              tableHeading={tableHeading}
              data={filteredData}
              viewDetailsHandler={viewDetailsHandler}
            />
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                marginTop: 18,
              }}>
              {totalCount > NUMBER_OF_ROWS_PER_PAGE && (
                <PaginationComponent
                  data-testid="pagination-component"
                  numberOfRowsPerPage={NUMBER_OF_ROWS_PER_PAGE}
                  count={totalCount}
                  onPageClick={onPageClick}
                  page={offset / 10}
                />
              )}
            </div>
          </>
        )}
      </Box>
      <ServiceFlyout
        isOpen={openFlyout}
        handleClose={closeFlyout}
        data={detailsData}
        icon={
          menuItems
            ? getIconFromSubItemsArray(
                menuItems?.Medical?.subItems,
                'Social History'
              )
            : null
        }
        category={SOCIAL_HISTORY.TITLE_TEXT}
        title={modalTitle}></ServiceFlyout>
      <SimpleBackdrop open={fetchAllSocialHistoryCall.loading} />
    </Content>
  );
};
